import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type FC,
  type ReactNode,
} from 'react';
import LoginPromptModal from './LoginPromptModal';

type PromptLoginOptions = {
  message?: string;
};

type LoginPromptContextValue = {
  isLoggedIn: boolean;
  promptLogin: (options?: PromptLoginOptions) => void;
};

const LoginPromptContext = createContext<LoginPromptContextValue | null>(null);

type LoginPromptProviderProps = {
  isLoggedIn: boolean;
  children: ReactNode;
};

export const LoginPromptProvider: FC<LoginPromptProviderProps> = ({
  isLoggedIn,
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState<string | undefined>(undefined);

  const promptLogin = useCallback((options?: PromptLoginOptions) => {
    setMessage(options?.message);
    setOpen(true);
  }, []);

  const handleHide = useCallback(() => {
    setOpen(false);
  }, []);

  const value = useMemo(
    () => ({ isLoggedIn, promptLogin }),
    [isLoggedIn, promptLogin]
  );

  return (
    <LoginPromptContext.Provider value={value}>
      {children}
      <LoginPromptModal
        visible={open}
        onHide={handleHide}
        message={message}
      />
    </LoginPromptContext.Provider>
  );
};

export const useLoginPrompt = (): LoginPromptContextValue => {
  const ctx = useContext(LoginPromptContext);
  if (!ctx) {
    return {
      isLoggedIn: true,
      promptLogin: () => {
        /* no-op when provider isn't mounted; action proceeds and backend enforces auth */
      },
    };
  }
  return ctx;
};
